<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>New Users</h1>

      <DataTable
        :value="newUsers"
        :loading="loading"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
      >
        <Column header="Created">
          <template #body="{ data }">
            {{ formatDate(data.created) }}
          </template>
        </Column>
        <Column header="Auth Method">
          <template #body="{ data }">
            <span v-if="data.authProviders.length">
              {{ data.authProviders.map(formatAuthProvider).join(', ') }}
            </span>
            <span v-else class="text-surface-400 italic">(unknown)</span>
          </template>
        </Column>
        <Column field="email" header="Email" />
        <Column field="orgName" header="Organization" />
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import dayjs from 'dayjs';
import type { AdminNewUserDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

const { api } = useApiClient();

const newUsers = ref<AdminNewUserDto[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);

function formatDate(created: number): string {
  return dayjs(created).format('MMM D, YYYY HH:mm');
}

function formatAuthProvider(provider: string): string {
  const map: Record<string, string> = {
    'google.com': 'Google',
    'apple.com': 'Apple',
    password: 'Email/Password',
  };
  return map[provider] ?? provider;
}

async function loadNewUsers() {
  loading.value = true;
  try {
    const { data, error } = await api.adminNewUserControllerList({
      query: {
        limit: String(currentLimit.value),
        offset: String(currentOffset.value),
      },
    });
    if (error) {
      throw new Error('Failed to load new users');
    }
    newUsers.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadNewUsers();
}

loadNewUsers();
</script>
